import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
  OnChanges,
  OnInit,
} from '@angular/core'

import { CommentingService } from '@app/features/scope-overview/service/commenting.service'
import Editor from 'ckeditor5-custom-build'
import _ from 'lodash'
import { User } from '@core/model/user.model'
import { AuthService } from '@core/service/auth.service'
import { ApplicationSetting } from '@core/model/enums/application-setting.enum'

export interface DescriptionUpdate {
  main: string
  extra: string
}

@Component({
  selector: 'description-editor',
  templateUrl: './description-editor.component.html',
  styleUrls: ['./description-editor.component.scss'],
})
export class DescriptionEditorComponent implements OnInit, OnChanges {
  @Input() model!: any
  @Input() disabled!: boolean
  @Input() visible!: boolean
  @Input() hideNote?: boolean = false
  @Input() showNoteBelow?: boolean = false
  @Input() showHideDescriptionOnly?: boolean = false
  @Input() descriptionRequired?: boolean = false
  @Input() descriptionDirty?: boolean = false
  @Input() disableDragging!: boolean
  @Input() extraDescription?: boolean = false
  @Input() set editable(value: boolean) {
    if (!_.isNil(value)) {
      this.isEditable = value
      if (!value) {
        this.cancelDescription()
        this.cancelNote()
      }
    }
  }

  @Output() visibleChange = new EventEmitter<boolean>()
  @Output() disableDraggingChange = new EventEmitter<boolean>()
  @Output() onSaveDescription = new EventEmitter<string>()
  @Output() onSaveExtraDescription = new EventEmitter<DescriptionUpdate>()
  @Output() onSaveNote = new EventEmitter<string>()

  @ViewChild('descriptionEditor') descriptionEditor: ElementRef

  description!: string
  internalNote!: string
  originalDescription!: string
  originalExtraDescription!: string
  originalInternalNote!: string
  tempExtraDesc = ''
  tempDesc = ''
  editingDescription = false
  editingNote = false
  commenting = false
  commentable: any = null
  isEditable = true
  extraDescriptionActive: boolean
  loggedInUser!: User

  constructor(
    public commentingService: CommentingService,
    private authService: AuthService
  ) {
    this.loggedInUser = this.authService.loggedInUser
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.model) this.setDescriptionValues()
  }

  ngOnInit() {
    this.extraDescriptionActive = false
  }

  setDescriptionValues() {
    this.internalNote = this.model?.internalNote
    this.originalDescription = this.model?.description
    this.originalExtraDescription = this.model?.extraDescription
    this.originalInternalNote = this.model?.internalNote

    if (!this.editingDescription) {
      if (this.extraDescription && this.extraDescriptionActive) {
        this.description = this.originalExtraDescription
      } else {
        this.description = this.originalDescription
      }
    }
  }

  showMainDescription() {
    this.extraDescriptionActive = false
    if (!_.isEmpty(this.description)) {
      this.tempExtraDesc = this.description
    }
    if (!_.isEmpty(this.tempDesc)) {
      this.description = this.tempDesc
    } else {
      this.description = this.model.description || ''
    }
  }

  showExtraDescription() {
    this.extraDescriptionActive = true
    if (!_.isEmpty(this.description)) {
      this.tempDesc = this.description
    }
    if (!_.isEmpty(this.tempExtraDesc)) {
      this.description = this.tempExtraDesc
    } else {
      this.description = this.model.extraDescription || ''
    }
  }

  showDescription($event?: Event) {
    if ($event) $event.stopPropagation()
    this.visible = true
    this.visibleChange.emit(this.visible)
    if (this.isEditable && !this.description) this.editDescription()
  }

  hideDescription($event?: Event) {
    if ($event) $event.stopPropagation()
    this.resetDescriptionHeight()
    this.visible = false
    this.visibleChange.emit(this.visible)
  }

  editDescription($event?: Event) {
    if ($event) $event.stopPropagation()
    this.visible = true
    if (this.extraDescriptionActive) {
      this.originalExtraDescription = this.description
    } else {
      this.originalDescription = this.description
    }
    this.editingDescription = true
  }

  saveDescription() {
    if (this.extraDescription) {
      if (this.extraDescriptionActive) {
        this.onSaveExtraDescription.emit({ main: this.tempDesc, extra: this.description })
        this.originalExtraDescription = this.description
        this.originalDescription = this.tempDesc
      } else {
        this.onSaveExtraDescription.emit({ main: this.description, extra: this.tempExtraDesc })
        this.originalDescription = this.description
        this.originalExtraDescription = this.tempExtraDesc
      }
    } else {
      this.onSaveDescription.emit(this.description)
      this.originalDescription = this.description
    }
    this.cancelDescription(true)
  }

  saveNote() {
    if (!this.disabled) {
      this.onSaveNote.emit(this.internalNote)
      this.originalInternalNote = this.internalNote
      this.cancelNote()
    }
  }

  editNote($event?: Event) {
    if ($event) $event.stopPropagation()
    if (!this.disabled) {
      this.originalInternalNote = this.internalNote
      this.editingNote = true
    }
  }

  cancelNote() {
    this.internalNote = this.originalInternalNote
    this.editingNote = false
  }

  cancelDescription(onSave = false) {
    this.editingDescription = false
    this.setOriginalDescriptions(onSave)
    if (!this.description) this.hideDescription()
    this.resetDescriptionHeight()
  }

  setOriginalDescriptions(onSave: boolean) {
    if (this.extraDescription && !onSave) {
      if (this.extraDescriptionActive) {
        this.description = this.originalExtraDescription
        this.tempDesc = this.originalDescription
      } else {
        this.description = this.originalDescription
        this.tempExtraDesc = this.originalExtraDescription
      }
    } else if (this.extraDescription && onSave) {
      if (this.extraDescriptionActive) {
        this.description = this.originalExtraDescription
        this.tempDesc = this.originalDescription
      } else {
        this.description = this.originalDescription
        this.tempExtraDesc = this.originalExtraDescription
      }
    } else {
      this.description = this.originalDescription
    }
  }

  setDisableDragging(value: boolean) {
    this.disableDragging = value
    this.disableDraggingChange.emit(value)
  }

  toggleCommenting() {
    this.commenting = !this.commenting
  }

  setCommentable(commentable) {
    this.commentable = commentable
  }

  resetDescriptionHeight() {
    if (this.descriptionEditor) this.descriptionEditor.nativeElement.style.height = this.getInternalNoteHeight()
  }

  getInternalNoteHeight = () => (this.internalNote || this.editingNote ? '122px' : '104px')

  protected readonly Editor = Editor as any
  protected readonly ApplicationSetting = ApplicationSetting
}
